import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import './Settings.css';

const Settings = () => {
    const { user, updateProfile, changePassword } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);

    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        profilePicture: user?.profilePicture || ''
    });

    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || '',
                profilePicture: user.profilePicture || ''
            });
        }
    }, [user]);

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [preferences, setPreferences] = useState({
        theme: user?.preferences?.theme || 'light',
        language: user?.preferences?.language || 'en',
        notifications: {
            email: user?.preferences?.notifications?.email ?? true,
            push: user?.preferences?.notifications?.push ?? true
        }
    });

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateProfile(profileData);
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error(error.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error('Passwords do not match');
        }
        setLoading(true);
        try {
            await changePassword(passwordData);
            toast.success('Password changed successfully');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handlePreferenceChange = async (key, value) => {
        const newPrefs = { ...preferences, [key]: value };
        setPreferences(newPrefs);
        try {
            await updateProfile({ preferences: newPrefs });
            toast.success('Preferences updated');
        } catch (error) {
            toast.error('Failed to update preferences');
        }
    };

    return (
        <DashboardLayout>
            <div className="settings-page">
                <div className="settings-header">
                    <h1>Settings</h1>
                    <p>Manage your account settings and preferences.</p>
                </div>

                <div className="settings-container">
                    <div className="settings-sidebar">
                        <button
                            className={`sidebar-link ${activeTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveTab('profile')}
                        >
                            <i className="fas fa-user"></i> Profile
                        </button>
                        <button
                            className={`sidebar-link ${activeTab === 'whatsapp' ? 'active' : ''}`}
                            onClick={() => setActiveTab('whatsapp')}
                        >
                            <i className="fab fa-whatsapp"></i> WhatsApp
                        </button>
                        <button
                            className={`sidebar-link ${activeTab === 'billing' ? 'active' : ''}`}
                            onClick={() => setActiveTab('billing')}
                        >
                            <i className="fas fa-wallet"></i> Billing & Wallet
                        </button>
                        <button
                            className={`sidebar-link ${activeTab === 'shopify' ? 'active' : ''}`}
                            onClick={() => setActiveTab('shopify')}
                        >
                            <i className="fab fa-shopify"></i> Shopify Store
                        </button>
                        <button
                            className={`sidebar-link ${activeTab === 'preferences' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preferences')}
                        >
                            <i className="fas fa-cog"></i> Preferences
                        </button>
                        {user?.registrationSource !== 'google' && (
                            <button
                                className={`sidebar-link ${activeTab === 'security' ? 'active' : ''}`}
                                onClick={() => setActiveTab('security')}
                            >
                                <i className="fas fa-shield-alt"></i> Security
                            </button>
                        )}
                    </div>

                    <div className="settings-content">
                        {activeTab === 'profile' && (
                            <div className="settings-section">
                                <h2>Profile Information</h2>
                                <form onSubmit={handleProfileUpdate}>
                                    <div className="form-group">
                                        <label>Full Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={profileData.name}
                                            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Email Address</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={profileData.email}
                                            disabled
                                        />
                                        <small>Email cannot be changed.</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Profile Picture URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={profileData.profilePicture}
                                            onChange={(e) => setProfileData({ ...profileData, profilePicture: e.target.value })}
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {activeTab === 'whatsapp' && (
                            <div className="settings-section">
                                <div className="section-header">
                                    <h2>WhatsApp Connection</h2>
                                    <div className={`connection-status ${user?.whatsappConnected ? 'active' : 'inactive'}`}>
                                        <span className="status-pulse"></span>
                                        {user?.whatsappConnected ? 'System Active' : 'Action Required'}
                                    </div>
                                </div>

                                <div className="whatsapp-v2-card">
                                    <div className="credentials-table-wrapper">
                                        <table className="credentials-table">
                                            <tbody>
                                                <tr>
                                                    <td><i className="fab fa-whatsapp"></i> Business Number</td>
                                                    <td className="value-cell">{user?.whatsappNumber || 'Not Connected'}</td>
                                                </tr>
                                                <tr>
                                                    <td><i className="fas fa-fingerprint"></i> Phone Number ID</td>
                                                    <td className="value-cell"><code>{user?.whatsappCredentials?.phoneNumberId || '—'}</code></td>
                                                </tr>
                                                <tr>
                                                    <td><i className="fas fa-id-card"></i> WABA ID</td>
                                                    <td className="value-cell"><code>{user?.whatsappCredentials?.businessAccountId || '—'}</code></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="whatsapp-actions">
                                        <button className="btn btn-whatsapp-v2" onClick={() => window.location.href = '/connect-whatsapp'}>
                                            <i className="fas fa-external-link-alt"></i>
                                            {user?.whatsappConnected ? 'Reconfigure Account' : 'Initialize Connection'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'billing' && (
                            <div className="settings-section">
                                <h2>Wallet & Billing</h2>
                                <div className="wallet-card">
                                    <div className="wallet-balance-info">
                                        <p>Current Balance</p>
                                        <h3>₹{user?.walletBalance?.toFixed(2) || '0.00'}</h3>
                                    </div>
                                    <button className="btn btn-success">Add Funds</button>
                                </div>

                                <div className="pricing-info mt-4">
                                    <h3>Your Messaging Rates</h3>
                                    <div className="pricing-grid">
                                        <div className="price-item">
                                            <span><i className="fas fa-bullhorn mr-2"></i> Marketing</span>
                                            <strong>₹{user?.messagePricing?.marketing?.toFixed(2) || '1.00'}</strong>
                                        </div>
                                        <div className="price-item">
                                            <span><i className="fas fa-tools mr-2"></i> Utility</span>
                                            <strong>₹{user?.messagePricing?.utility?.toFixed(2) || '0.20'}</strong>
                                        </div>
                                        <div className="price-item">
                                            <span><i className="fas fa-key mr-2"></i> Authentication</span>
                                            <strong>₹{user?.messagePricing?.authentication?.toFixed(2) || '0.15'}</strong>
                                        </div>
                                        <div className="price-item">
                                            <span><i className="fas fa-comment-alt mr-2"></i> Standard Text</span>
                                            <strong>₹{user?.messagePricing?.text?.toFixed(2) || '0.10'}</strong>
                                        </div>
                                    </div>
                                    <p className="pricing-note mt-2" style={{ fontSize: '11px', color: '#6b7280' }}>
                                        <i className="fas fa-info-circle mr-1"></i> Rates are per unique conversation success. GST (18%) will be applied.
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'preferences' && (
                            <div className="settings-section">
                                <h2>Preferences</h2>
                                {/* <div className="preference-item">
                                    <div>
                                        <strong>Appearance</strong>
                                        <p>Choose your preferred theme.</p>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={preferences.theme}
                                        onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                                    >
                                        <option value="light">Light Mode</option>
                                        <option value="dark">Dark Mode</option>
                                    </select>
                                </div> */}
                                <div className="preference-item">
                                    <div>
                                        <strong>Language</strong>
                                        <p>Choose your display language.</p>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={preferences.language}
                                        onChange={(e) => handlePreferenceChange('language', e.target.value)}
                                        disabled
                                    >
                                        <option value="en">English</option>
                                        <option value="es">Spanish</option>
                                        <option value="hi">Hindi</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {activeTab === 'shopify' && (
                            <div className="settings-section">
                                <div className="section-header">
                                    <h2>Shopify Integration</h2>
                                    <div className={`connection-status ${user?.shopifyDetails?.isActive ? 'active' : 'inactive'}`}>
                                        <span className="status-pulse"></span>
                                        {user?.shopifyDetails?.isActive ? 'Linked' : 'Not Connected'}
                                    </div>
                                </div>

                                <div className="shopify-card">
                                    <div className="shopify-info-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
                                        <i className="fab fa-shopify" style={{ fontSize: '3.5rem', color: '#95bf47', marginRight: '1.5rem' }}></i>
                                        <div className="shopify-text">
                                            <h3 style={{ margin: 0 }}>Automate Your Store</h3>
                                            <p style={{ margin: 0, opacity: 0.8 }}>Send instant WhatsApp updates for new orders and recover abandoned carts.</p>
                                        </div>
                                    </div>

                                    {!user?.shopifyDetails?.isActive ? (
                                        <div className="shopify-connect-form">
                                            <div className="form-group mb-4">
                                                <label className="d-block mb-2">Shopify Store Domain</label>
                                                <div className="input-with-suffix" style={{ display: 'flex', alignItems: 'stretch' }}>
                                                    <input 
                                                        type="text" 
                                                        id="shopDomainInput"
                                                        placeholder="my-cool-store" 
                                                        style={{ flex: 1, padding: '0.75rem', borderRadius: '8px 0 0 8px', border: '1px solid #ddd' }}
                                                    />
                                                    <span style={{ padding: '0.75rem', background: '#f8f9fa', border: '1px solid #ddd', borderLeft: 'none', borderRadius: '0 8px 8px 0', fontSize: '0.9rem' }}>.myshopify.com</span>
                                                </div>
                                                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>Enter your shop subdomain name.</small>
                                            </div>
                                            <button 
                                                className="btn btn-success"
                                                style={{ backgroundColor: '#95bf47', borderColor: '#95bf47', padding: '0.75rem 2rem', fontWeight: 'bold' }}
                                                onClick={() => {
                                                    const shopName = document.getElementById('shopDomainInput').value;
                                                    if (!shopName) return toast.error('Please enter store name');
                                                    const shop = shopName.includes('.') ? shopName : `${shopName}.myshopify.com`;
                                                    window.location.href = `${process.env.REACT_APP_API_URL}/shopify/auth?shop=${shop}&token=${localStorage.getItem('token')}`;
                                                }}
                                            >
                                                Connect Shopify Store
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="shopify-connected-details">
                                            <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span style={{ color: '#666' }}>Connected Store:</span>
                                                    <strong>{user.shopifyDetails.shopDomain}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span style={{ color: '#666' }}>Connected Since:</span>
                                                    <strong>{new Date(user.shopifyDetails.connectedAt).toLocaleDateString()}</strong>
                                                </div>
                                            </div>
                                            <div className="shopify-status-list">
                                                <div style={{ color: '#28a745', marginBottom: '0.5rem' }}><i className="fas fa-check-circle mr-2"></i> Abandoned Cart Webhooks Active</div>
                                                <div style={{ color: '#28a745' }}><i className="fas fa-check-circle mr-2"></i> Order Management Synchronized</div>
                                            </div>
                                            <button 
                                                className="btn btn-outline-danger btn-sm mt-4"
                                                onClick={() => toast.info('Disconnect functionality is being finalized.')}
                                            >
                                                Disconnect Store
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'security' && user?.registrationSource !== 'google' && (
                            <div className="settings-section">
                                <h2>Security Settings</h2>
                                <form onSubmit={handlePasswordChange}>
                                    <div className="form-group">
                                        <label>Current Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.currentPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>New Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.newPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Confirm New Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? 'Updating...' : 'Change Password'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>


        </DashboardLayout>
    );
};

export default Settings;
